import "@tento-ui/components/transition";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Toast } from "@tento-ui/components/toast/toast";
import { Transition } from "@tento-ui/components/transition";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ResponseError } from "@tento-core/typed-api";
import { LoadingIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Pip } from "@tento-ui/ui/pip";
import { LonaApi } from "../api";
import { GarminSuccessPage } from "./garmin-success.page";
import { Tracing } from "@tento-core/tracing";

@component({
  name: "lona-oauth-page",
})
export class OAuthPage extends LonaWebComponent {
  onLinkSuccess: Option<() => void>;
  private $submitTransition: Transition<HTMLButtonElement, HTMLElement> =
    this.hydrate("submit-transition");
  private $submitButton: Option<HTMLButtonElement> = null;

  static async show(props: OAuthPage.Props) {
    const $page = OAuthPage.make().bind(props);
    const win = await Pip.show($page, {
      margin: "0px",
      theme: "light",
      size: {
        height: 480,
        width: 360,
      },
    });
    $page.onLinkSuccess = () => {
      Toast.info(`You've linked your ${props.integration} account`);
      win?.close();
      props.onSuccess();
    };
    // if (Globals.isServiceAccount) {
    //   setTimeout(() => {
    //     $page.onLinkSuccess?.();
    //     // $page.onLinkFailure(props, {
    //     //   code: 0,
    //     //   inner: {
    //     //     detail: "Uh Oh",
    //     //     reason: "something went wrong",
    //     //   },
    //     // });
    //   }, 1_000);
    // }
  }

  bind(props: OAuthPage.Props): this {
    if (this.$submitButton) GESTURE_MANAGER.unregister(this.$submitButton);

    this.$("error").style.display = "none";
    this.$<HTMLImageElement>("logo").src = props.imgUrl;
    this.$("title").textContent = props.title;

    this.$submitButton = document.createElement("button");
    this.$submitButton.type = "button";
    this.$submitButton.textContent = "Continue";

    const $spinner = this.createSpinner();

    this.$submitTransition.bind({
      state: "before",
      $before: this.$submitButton,
      $after: $spinner,
    });

    GESTURE_MANAGER.addPointerEvent(this.$submitButton, {
      onClick: async () => {
        this.$submitTransition.bindState("after");
        const r = await LonaApi.postResult("/api/user/connections/{source}", {
          path: {
            source: "garmin",
          },
          body: {
            credentials: {
              username: this.$<HTMLInputElement>("email").value,
              password: this.$<HTMLInputElement>("password").value,
            },
          },
        });
        r.match({
          ok: () => this.onLinkSuccess?.(),
          err: (e) => {
            this.$submitTransition.bindState("before");
            this.onLinkFailure(props, e);
          },
        });
      },
    });
    return this;
  }

  private createSpinner(): HTMLElement {
    const $spinner = document.createElement("div");
    $spinner.className = "spinner-container";
    const $icon = LonaIcons.make(LoadingIcon, {
      sizePx: 24,
    });
    $spinner.appendChild($icon);
    return $spinner;
  }

  onLinkFailure(props: OAuthPage.Props, e: ResponseError) {
    // todo: use more description error?
    //
    Tracing.err("ui")("oauth-error", e);
    this.$("error").style.display = "block";
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        font-family: sans-serif;
        display: block;
      }

      #root {
        height: calc(480px - 16px);
        width: calc(360px - 16px);
        background: var(--card-background-color, #fff);
        border-radius: 12px;
        margin: 8px;
        padding: 24px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      #logo {
        margin-top: 48px;
        max-height: 48px;
        max-width: 80%;
        object-fit: contain;
      }

      #title {
        font-size: 1.25rem;
        text-align: center;
        margin: 0;
        color: var(--text-color, #222);
        margin-bottom: 12px;
      }

      #login-form {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 12px;
      }

      #login-form input {
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 1rem;
        background: #fafafa;
      }

      #login-form input:focus {
        outline: none;
        border-color: #007aff;
        background: var(--bg);
      }

      #submit-transition button {
        width: 100%;
        padding: 10px;
        background-color: #007aff;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 1rem;
        cursor: pointer;
        transition: background 0.2s ease;
      }

      #submit-transition button:hover {
        background-color: #005fcc;
      }

      .spinner-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 10px;
      }

      #spacer {
        height: 36px;
        width: 100%;
        flex-direction: column-reverse;
      }

      #error {
        justify-self: flex-end;
        border-radius: 2px;
        color: #be4846;
        // color: white;
        width: 100%;
        padding-inline: 8px;
        display: none;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <img id="logo" />
      <h1 id="title" class="h6"></h1>
      <form id="login-form">
        <std-col id=spacer>
          <std-col id=error>
            <p>Something went wrong. Please wait and try again later.</p>
          </std-col>
        </std-col>
        <input type="email" id="email" placeholder="Email" required />
        <input type="password" id="password" placeholder="Password" required />
        <std-transition id="submit-transition"></std-transition>
      </form>
    </std-col>
  `;
}

export namespace OAuthPage {
  export type Props = {
    integration: string;
    title: string;
    imgUrl: string;
    onSuccess: () => void;
  };

  export const GARMIN: Props = {
    integration: "Garmin",
    title: "Link your Garmin account",
    imgUrl: "https://lona.so/@/static/img/integrations/garmin.svg",
    onSuccess: () => {
      setTimeout(() => {
        GarminSuccessPage.reveal(() => {});
      }, 500);
    },
  };
}
